import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { getAllGrowers, saveGrower, getOrganizations } from '../../lib/api';
import { Table, TableCell } from '../../components/table/Table';
import { Connect as DeereConnect } from '../deere/Connect';
import * as deere from '../../services/deere-service';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { useAlerts } from '../../hooks/use-alerts';
import { OperationDetails } from './OperationDetails';
import { useCurrentUser } from '../../hooks/use-current-user';

const newGrower = {
  operationName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  officePhone: '',
  zipCode: '',
  organizationId: '',
  isActive: true,
};

export default function GrowerManagement() {
  const [growers, setGrowers] = useState([]);
  const [currentGrower, setCurrentGrower] = useState(null);
  const [growerToConnect, setGrowerToConnect] = useState(null);
  const [growerToDisconnect, setGrowerToDisconnect] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const user = useCurrentUser();

  const [isLoading, setIsLoading] = useState(true);
  const { showError } = useAlerts();

  useEffect(() => {
    getAllGrowers().then((all) => {
      setGrowers(all);
      setIsLoading(false);
    });

    getOrganizations().then((orgs) => {
      setOrganizations(orgs);
    });
  }, []);

  return (
    <Box
      sx={{ p: 4, display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {buildHeader()}
      {buildTable()}
      {buildNewGrowerDialog()}
      {buildDeereConnect()}
      {buildDeereDisconnect()}
    </Box>
  );

  function buildNewGrowerDialog() {
    if (!currentGrower) return null;

    return (
      <OperationDetails
        operation={currentGrower}
        organizations={organizations}
        handleSave={save}
        handleClose={closeDialog}
        isAdmin={user.isAdmin}
      />
    );
  }

  function buildDeereConnect() {
    if (!growerToConnect) return null;

    return (
      <DeereConnect
        grower={growerToConnect}
        onConnected={growerConnected}
        onCancel={() => setGrowerToConnect(null)}
      />
    );
  }

  async function disconnectGrower() {
    try {
      await deere.disconnect(growerToDisconnect);
      setGrowers((current) =>
        current.map((grower) =>
          grower.guid === growerToDisconnect.guid
            ? { ...grower, deereId: null }
            : grower,
        ),
      );
      setGrowerToDisconnect(null);
    } catch (e) {
      showError(e);
    }
  }

  function buildDeereDisconnect() {
    if (!growerToDisconnect) return null;

    return (
      <ConfirmationDialog
        title="Disconnect Operation?"
        message={`Are you sure you want to disconnect ${growerToDisconnect.operationName} from ${growerToDisconnect.deereId}`}
        handleClose={() => setGrowerToDisconnect(null)}
        handleConfirm={() => disconnectGrower()}
      />
    );
  }

  function growerConnected(deereId) {
    setGrowers((current) =>
      current.map((grower) =>
        grower.guid === growerToConnect.guid ? { ...grower, deereId } : grower,
      ),
    );
    setGrowerToConnect(null);
  }

  function save(grower) {
    setIsLoading(true);

    saveGrower(grower).then(() => {
      getAllGrowers().then((all) => {
        setGrowers(all);
        setIsLoading(false);
        setCurrentGrower(null);
      });
    });
  }

  function editGrower(grower, e) {
    e.preventDefault();
    setCurrentGrower(grower);
  }

  function closeDialog() {
    setCurrentGrower(null);
  }

  function buildHeader() {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ display: 'inline-block', flex: 1 }} variant="h5">
          Operation Management
        </Typography>
        <Button
          onClick={() => setCurrentGrower(newGrower)}
          variant="contained"
          color="primary"
        >
          Add New Operation
        </Button>
      </Box>
    );
  }

  function buildTable() {
    return (
      <Table
        loading={isLoading}
        hideFooter={false}
        rows={growers}
        columns={buildTableColumns()}
        initialState={{
          pinnedColumns: { right: ['actions'] },
          sorting: {
            sortModel: [{ field: 'operationName', sort: 'asc' }],
          },
        }}
        localeText={{ footerTotalRows: 'Total Operations:' }}
      />
    );
  }

  function buildTableColumns() {
    return [
      {
        field: 'operationName',
        headerName: 'Name',
        flex: 1.25,
      },
      {
        field: 'addressLine1',
        headerName: 'Address',
        flex: 1.25,
      },
      {
        field: 'city',
        headerName: 'City',
        flex: 0.75,
      },
      {
        field: 'state',
        headerName: 'State',
        flex: 0.5,
      },
      {
        field: 'zipCode',
        headerName: 'ZIP',
        type: 'number',
        flex: 0.5,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'organization',
        headerName: 'Organization',
        flex: 1.25,
        valueGetter: (params) => params.row.organization.name,
      },
      {
        field: 'guid',
        headerName: 'GUID',
        flex: 1.25,
        renderCell: (params) => (
          <TableCell
            copyTarget="GUID"
            copyOnClick={params.row.guid}
            renderCellParams={params}
          />
        ),
      },
      {
        field: 'slackWebHook',
        headerName: 'Slack Webhook',
        flex: 1.25,
      },
      {
        field: 'deereId',
        headerName: 'Deere Organization',
        flex: 1,
        renderCell: (params) => (
          <TableCell renderCellParams={params}>
            {params.row.deereId
              ? buildDeereDisconnectButton(params.row)
              : buildDeereConnectButton(params.row)}
          </TableCell>
        ),
      },
      {
        field: 'isActive',
        headerName: 'Active',
        valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
      },
      {
        field: 'edit',
        headerName: 'Edit',
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <Button
            onClick={editGrower.bind(null, row)}
            variant="outlined"
            color="inherit"
            data-testid="edit-grower"
          >
            Edit
          </Button>
        ),
      },
    ].filter(Boolean);
  }

  function buildDeereDisconnectButton(row) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <IconButton color="inherit" onClick={() => setGrowerToDisconnect(row)}>
          <DeleteIcon />
        </IconButton>
        <Typography>{row.deereId}</Typography>
      </Box>
    );
  }

  function buildDeereConnectButton(row) {
    return (
      <Button
        onClick={() => setGrowerToConnect(row)}
        variant="outlined"
        color="inherit"
      >
        Connect
      </Button>
    );
  }
}
